import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOutOptimistic } from '../../../services/auth/authApi';
import { getStoredSession } from '../../../services/api/sessionApi';
import { useConversation } from '../../../hooks';
import { useScope } from '../../../context/PermissionContext';
import { LogoutIcon } from '../icons';
import { ROUTES } from '../../../constants/routes';
import './UserProfilePanel.css';

// REVIEW-FORM / R1 — inline star icon for the "Rate ArogyaKhata" menu item.
// Matches the LogoutIcon pattern (inherits currentColor, 16px default).
function StarIcon({ size = 16 }) {
  return (
    <svg
      style={{ width: size, height: size, flexShrink: 0 }}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

// OR-138 — "About" menu item icon (info circle), same inline-SVG pattern.
function InfoIcon({ size = 16 }) {
  return (
    <svg
      style={{ width: size, height: size, flexShrink: 0 }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

// Admin-only "Settings" menu item icon — simple gear outline, matches the
// StarIcon/LogoutIcon inline-SVG pattern (inherits currentColor, 16px default).
function GearIcon({ size = 16 }) {
  return (
    <svg
      style={{ width: size, height: size, flexShrink: 0 }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// Inline spinner — matches the SVG pattern in LogoutButton.js for visual
// consistency. Inherits currentColor so it adapts to the dropdown's text color
// in both light and dark mode.
function Spinner({ size = 16 }) {
  return (
    <svg
      className="animate-spin"
      style={{ width: size, height: size, flexShrink: 0 }}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

const UserProfilePanel = () => {
  const navigate = useNavigate();
  const { resetConversations } = useConversation();
  const { isAdmin } = useScope();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // All profile data comes from the in-memory session cache.
    // PII (email, phone) is held in memory only — not in localStorage.
    const session = getStoredSession();
    if (session) {
      setProfile({
        displayName: session.displayName || null,
        username: session.username || '',
        email: session.email || null,
        phone: session.phone || null,
        userType: session.userType || 'INDIVIDUAL',
        onboardingCompleted: session.onboarding_completed || false,
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Avatar initials — prefer displayName, fall back to email initial or phone digits
  const getInitials = () => {
    if (!profile) return '?';
    if (profile.displayName) {
      const parts = profile.displayName.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0].slice(0, 2).toUpperCase();
    }
    if (profile.email) return profile.email[0].toUpperCase();
    if (profile.phone) {
      const digits = profile.phone.replace(/\D/g, '');
      return digits.slice(-2) || '?';
    }
    const u = profile.username || '';
    if (u.includes('@')) return u[0].toUpperCase();
    return u.slice(0, 2).toUpperCase() || '?';
  };

  // Top line — display name if available, otherwise username
  const topLine = profile?.displayName || profile?.username || '';

  const isOrg = profile?.userType === 'ORGANISATION';

  const handleLogout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    resetConversations();
    // Optimistic: local auth state is cleared synchronously inside
    // signOutOptimistic(); the network revoke fires in the background.
    signOutOptimistic().catch((err) =>
      console.warn('[UserProfilePanel] background revoke failed:', err?.message)
    );
    navigate(ROUTES.LOGIN);
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  if (!profile) {
    return (
      <div className="user-profile-panel">
        <div className="profile-avatar-button" style={{ opacity: 0.5 }}>
          <div className="profile-avatar">
            <span className="avatar-initials">…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-panel" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        className="profile-avatar-button"
        onClick={toggleDropdown}
        aria-label="User Profile"
        aria-expanded={isOpen}
      >
        <div className="profile-avatar">
          <span className="avatar-initials">{getInitials()}</span>
          <div className="avatar-status-indicator" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="profile-dropdown">
          <div className="profile-dropdown-header">
            {/* Display name (top line) */}
            {topLine && (
              <div className="profile-name">{topLine}</div>
            )}

            {/* Email */}
            {profile.email && (
              <div className="profile-contact">{profile.email}</div>
            )}

            {/* Phone */}
            {profile.phone && (
              <div className={profile.email ? 'profile-contact-secondary' : 'profile-contact'}>
                {profile.phone}
              </div>
            )}

            {/* If neither email nor phone, show username as contact */}
            {!profile.email && !profile.phone && profile.username && (
              <div className="profile-contact">{profile.username}</div>
            )}

            {/* User type badge */}
            {isOrg ? (
              <div className="profile-org">
                <span className="profile-org-badge">ORG</span>
                Organisation
              </div>
            ) : (
              <div className="profile-type-badge">Individual</div>
            )}
          </div>

          <div className="profile-dropdown-divider" />

          {/* Admin-only "Settings" entry point — same isAdmin bar as
              AdminRoleGate/SettingsPage. Sits above "Rate ArogyaKhata". */}
          {isAdmin && (
            <>
              <button
                className="profile-dropdown-item"
                onClick={() => { setIsOpen(false); navigate(ROUTES.SETTINGS); }}
              >
                <GearIcon size={16} />
                <span>Settings</span>
              </button>

              <div className="profile-dropdown-divider" />
            </>
          )}

          {/* OR-138 — "About" (every role), next to Settings. */}
          <button
            className="profile-dropdown-item"
            onClick={() => { setIsOpen(false); navigate(ROUTES.ABOUT); }}
          >
            <InfoIcon size={16} />
            <span>About</span>
          </button>

          <div className="profile-dropdown-divider" />

          {/* REVIEW-FORM / R1 — "Rate ArogyaKhata" entry point, directly
              above Logout. */}
          <button
            className="profile-dropdown-item"
            onClick={() => { setIsOpen(false); navigate(ROUTES.REVIEW); }}
          >
            <StarIcon size={16} />
            <span>Rate ArogyaKhata</span>
          </button>

          <div className="profile-dropdown-divider" />

          <button
            className="profile-dropdown-item"
            onClick={handleLogout}
            disabled={loggingOut}
            aria-busy={loggingOut}
            style={loggingOut ? { cursor: 'wait', opacity: 0.7 } : undefined}
          >
            {loggingOut ? <Spinner size={16} /> : <LogoutIcon size={16} />}
            <span>{loggingOut ? 'Signing out…' : 'Logout'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfilePanel;
